export function escapeHtml(input: string): string {
  return (input || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Split a markdown table row on `|` delimiters while respecting markdown links.
 * A `|` inside `[text](url)` is treated as literal content, not a cell boundary.
 */
function splitTableCells(row: string): string[] {
  const cells: string[] = [];
  let current = "";
  let linkUrlDepth = 0;

  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === "(" && i > 0 && row[i - 1] === "]") {
      // `](` — entering a markdown link URL
      linkUrlDepth++;
    } else if (ch === "(" && linkUrlDepth > 0) {
      // nested paren inside link URL (e.g. Wikipedia)
      linkUrlDepth++;
    } else if (ch === ")" && linkUrlDepth > 0) {
      linkUrlDepth--;
    } else if (ch === "|" && linkUrlDepth === 0) {
      cells.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells;
}

function renderTables(text: string): string {
  // Matches GitHub-style pipe tables
  const tableRegex = /^\|(.+)\|[ \t]*\n\|[-| \t:]+\|[ \t]*\n((?:\|.+\|[ \t]*\n?)*)/gm;

  return text.replace(tableRegex, (match, headerRow, dataRows) => {
    const headers = splitTableCells(String(headerRow))
      .map(h => h.trim())
      .filter(h => h);

    const rows = String(dataRows)
      .trim()
      .split("\n")
      .map(row => {
        const c = splitTableCells(row).map(cell => cell.trim());
        return c.slice(1, c.length - 1);
      })
      .filter(row => row.length > 0);

    // Wrap tables so we can apply horizontal scrolling without affecting the rest of the chat layout
    let tableHtml = '<div class="markdown-table-scroll"><table class="markdown-table">';
    tableHtml += "<thead><tr>" + headers.map(h => `<th>${h}</th>`).join("") + "</tr></thead>";
    tableHtml +=
      "<tbody>" +
      rows.map(r => "<tr>" + r.map(c => `<td>${c}</td>`).join("") + "</tr>").join("") +
      "</tbody>";
    tableHtml += "</table></div>";
    return tableHtml;
  });
}

function renderLists(text: string): string {
  const lines = text.split("\n");
  const result: string[] = [];
  // Stack tracks indent levels and tag type of open list elements
  const depthStack: Array<{ indent: number; tag: "ul" | "ol" }> = [];
  let listHtml = "";

  function closeListsTo(targetDepth: number) {
    while (depthStack.length > 0 && depthStack[depthStack.length - 1].indent >= targetDepth) {
      const { tag } = depthStack.pop()!;
      listHtml += `</li></${tag}>`;
    }
  }

  function flushList() {
    if (depthStack.length === 0) return;
    closeListsTo(-1);
    result.push(listHtml);
    listHtml = "";
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Match unordered (- item) or ordered (1. item) list items
    const ulMatch = line.match(/^(\s*)-\s+(.*)/);
    const olMatch = !ulMatch ? line.match(/^(\s*)\d+\.\s+(.*)/) : null;
    const match = ulMatch || olMatch;
    const tag: "ul" | "ol" = ulMatch ? "ul" : "ol";

    if (match) {
      const indent = match[1].length;
      const content = match[2];

      if (depthStack.length === 0) {
        // Start a new list
        listHtml += `<${tag}><li>` + content;
        depthStack.push({ indent, tag });
      } else {
        const current = depthStack[depthStack.length - 1];
        if (indent > current.indent) {
          // Nest deeper — open a child list inside the current <li>
          listHtml += `<${tag}><li>` + content;
          depthStack.push({ indent, tag });
        } else if (indent < current.indent) {
          // Outdent — close lists back to this level, then add sibling
          closeListsTo(indent + 1);
          listHtml += "</li><li>" + content;
        } else if (tag !== current.tag) {
          // Same indent but different list type — close current, start new
          flushList();
          listHtml += `<${tag}><li>` + content;
          depthStack.push({ indent, tag });
        } else {
          // Same level, same type — sibling item
          listHtml += "</li><li>" + content;
        }
      }
    } else {
      flushList();
      result.push(line);
    }
  }

  flushList();
  return result.join("\n");
}

function renderBlockquotes(text: string): string {
  // Split into lines to process blockquotes
  const lines = text.split("\n");
  const result: string[] = [];
  let inBlockquote = false;
  let blockquoteContent: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check if line starts with >
    if (/^\s*&gt;\s*/.test(line)) {
      // Remove the > prefix and add to blockquote content
      const content = line.replace(/^\s*&gt;\s?/, "");
      blockquoteContent.push(content);
      inBlockquote = true;
    } else {
      // If we were in a blockquote, close it
      if (inBlockquote) {
        result.push("<blockquote>" + blockquoteContent.join("<br>") + "</blockquote>");
        blockquoteContent = [];
        inBlockquote = false;
      }
      result.push(line);
    }
  }

  // Close any remaining blockquote
  if (inBlockquote) {
    result.push("<blockquote>" + blockquoteContent.join("<br>") + "</blockquote>");
  }

  return result.join("\n");
}

/**
 * Promote label-style list items (e.g. "- Sophomore Year:") to headings when
 * they have deeper-indented sub-items underneath. This runs on raw escaped text
 * before heading/list rendering so the existing regex and renderLists handle
 * the converted output naturally.
 */
function promoteListLabels(text: string): string {
  const lines = text.split("\n");
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Match a bullet item whose content ends with ":"
    const match = line.match(/^(\s*)-\s+(.+):\s*$/);

    if (match) {
      const indent = match[1].length;
      const label = match[2];

      // Check if the next non-blank line is a deeper-indented list item (bullet or numbered)
      let j = i + 1;
      while (j < lines.length && /^\s*$/.test(lines[j])) j++;
      const nextBullet = j < lines.length ? lines[j].match(/^(\s*)(?:-|\d+\.)\s+/) : null;

      if (nextBullet && nextBullet[1].length > indent) {
        // Convert to heading: depth 0 → ###, depth 1+ → ####
        const level = indent === 0 ? "###" : "####";
        result.push(`${level} ${label}`);
        continue;
      }
    }

    result.push(line);
  }

  return result.join("\n");
}

export function renderMarkdown(input: string): string {
  // Start with escaped HTML for safety
  let s = escapeHtml(input || "");

  // Tables first
  s = renderTables(s);

  // Promote label-style list items to headings before heading/list rendering
  s = promoteListLabels(s);

  // Horizontal rules
  s = s.replace(/^\s*([-*_]){3,}\s*$/gm, "<hr />");

  // Headings (order matters — longest prefix first so ###### isn't caught by ##)
  s = s.replace(/^[ \t]*###### (.+)$/gm, "<h6>$1</h6>");
  s = s.replace(/^[ \t]*##### (.+)$/gm, "<h5>$1</h5>");
  s = s.replace(/^[ \t]*#### (.+)$/gm, "<h4>$1</h4>");
  s = s.replace(/^[ \t]*### (.+)$/gm, "<h3>$1</h3>");
  s = s.replace(/^[ \t]*## (.+)$/gm, "<h2>$1</h2>");
  s = s.replace(/^[ \t]*# (.+)$/gm, "<h1>$1</h1>");

  // Code blocks ```code```
  s = s.replace(/```([\s\S]*?)```/g, (_m, p1) => `<pre><code>${p1}</code></pre>`);

  // Inline code `code`
  s = s.replace(/`([^`]+)`/g, (_m, p1) => `<code>${p1}</code>`);

  // Bold and italics
  s = s.replace(/\*\*([^*]+)\*\*/g, (_m, p1) => `<strong>${p1}</strong>`);
  s = s.replace(/\*([^*]+)\*/g, (_m, p1) => `<em>${p1}</em>`);

  // Links [text](url) — supports balanced parens in URLs (e.g. Wikipedia) and www. without protocol
  s = s.replace(/\[([^\]]+)\]\(([^()\s<>]*(?:\([^)]*\)[^()\s<>]*)*)\)/g, (_m, text, url) => {
    if (!url) return _m;
    // Internal links (starting with /) use SPA navigation — no target="_blank"
    if (url.startsWith("/")) {
      // School links get a star button for quick pipeline add
      const schoolMatch = url.match(/^\/schools\?id=([^&]+)/);
      if (schoolMatch) {
        const schoolId = schoolMatch[1];
        return `<span class="school-mention"><a href="${url}" data-internal-link>${text}</a><button class="school-star" data-school-id="${schoolId}" data-school-name="${text}" aria-label="Add to My Schools"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg></button></span>`;
      }
      return `<a href="${url}" data-internal-link>${text}</a>`;
    }
    const href = /^https?:/.test(url) ? url : url.startsWith("www.") ? "https://" + url : url;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });

  // Auto-linked angle-bracket URLs: <https://...> (angle brackets were escaped by escapeHtml)
  s = s.replace(
    /&lt;(https?:\/\/[^&\s]+)&gt;/g,
    (_m, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );

  // Bare URLs not already inside an <a> tag
  s = s.replace(
    /(?<![="'>])(https?:\/\/[^\s<,)\]]+)/g,
    (_m, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );

  // Lists (process before line breaks to handle multi-line lists)
  s = renderLists(s);

  // Blockquotes (process before line breaks to handle multi-line blockquotes)
  s = renderBlockquotes(s);

  // Collapse runs of blank lines into a single blank line
  s = s.replace(/\n{3,}/g, "\n\n");

  // Line breaks — convert remaining newlines to <br>, but skip redundant breaks around block elements
  const lines = s.split("\n");
  const result: string[] = [];
  const blockEnd = /(<\/(?:h[1-6]|ul|ol|pre|blockquote|table|div)>|<hr \/>)\s*$/;
  const blockStart = /^\s*(<(?:h[1-6]|ul|ol|pre|blockquote|table|div|hr)[>\s/])/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isBlank = /^\s*$/.test(line);

    // Convert blank lines to a <br> for spacing, but skip if adjacent to a block element
    // (block elements already have their own margins via CSS)
    if (isBlank) {
      const prevLine = i > 0 ? lines[i - 1] : "";
      const nextLine = i < lines.length - 1 ? lines[i + 1] : "";
      const afterBlock = blockEnd.test(prevLine);
      const beforeBlock = blockStart.test(nextLine);
      if (!afterBlock && !beforeBlock && i > 0 && i < lines.length - 1) {
        result.push("<br>");
      }
      continue;
    }

    result.push(line);

    // Add <br> between consecutive non-blank, non-block lines
    if (i < lines.length - 1) {
      const nextLine = lines[i + 1];
      const nextIsBlank = /^\s*$/.test(nextLine);
      if (!blockEnd.test(line) && !blockStart.test(nextLine) && !nextIsBlank) {
        result.push("<br>");
      }
    }
  }
  s = result.join("");

  return s;
}
